import { useState, useEffect } from 'react'
import React from 'react'
import {
  useDataProvider,
  useNotify,
  useRecordContext,
  useRefresh,
} from 'react-admin'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Paper,
  Grid,
  Pagination,
  Drawer,
  IconButton,
  Button,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import RefreshIcon from '@mui/icons-material/Refresh'

import CustomAudioField from '../../components/CustomAudioField'

const SentencesTab = () => {
  const record = useRecordContext()
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()

  const [sentences, setSentences] = useState([])
  const [loading, setLoading] = useState(false)
  const [chapters, setChapters] = useState([])

  // Voice selection state
  const [selectedVoice, setSelectedVoice] = useState('')

  // Get available voices from the book record
  const availableVoices = record?.voices || []

  // Helper function to get voice-specific sentence data
  const getVoiceSentenceData = (sentence, selectedVoice) => {
    if (!sentence?.voices || !selectedVoice) {
      return {
        speakRate: sentence?.speakRate || 0,
        qcDiffResult: sentence?.qcDiffResult || [],
        qcAccuracyPercentage: sentence?.qcAccuracyPercentage || 0,
        status: sentence?.status || 'processing',
        audioPath: sentence?.audioPath || '',
      }
    }

    const voiceObj = sentence.voices.find(v => v.voice === selectedVoice)
    return {
      speakRate: voiceObj?.speakRate || sentence?.speakRate || 0,
      qcDiffResult: voiceObj?.qcDiffResult || sentence?.qcDiffResult || [],
      qcAccuracyPercentage:
        voiceObj?.qcAccuracyPercentage || sentence?.qcAccuracyPercentage || 0,
      status: voiceObj?.status || sentence?.status || 'processing',
      audioPath: voiceObj?.audioPath || sentence?.audioPath || '',
    }
  }

  // Helper function to get voice-specific audio path
  const getVoiceAudioPath = (sentence, selectedVoice) => {
    if (!sentence?.voices || !selectedVoice) {
      return sentence?.audioPath || ''
    }

    const voiceObj = sentence.voices.find(v => v.voice === selectedVoice)
    return voiceObj?.audioPath || sentence?.audioPath || ''
  }

  // Pagination state
  const [page, setPage] = useState(1)
  const [rowsPerPage] = useState(50)

  // Aside panel state
  const [selectedSentence, setSelectedSentence] = useState(null)
  const [showAsidePanel, setShowAsidePanel] = useState(false)

  // Sentence form state for editing
  const [sentenceFormData, setSentenceFormData] = useState({
    speakRate: '',
    contentWithBreaks: '',
  })

  // Filter and sort state
  const [filters, setFilters] = useState({
    minAccuracy: '',
    maxAccuracy: '',
    chapterId: '',
  })
  const [sortConfig, setSortConfig] = useState({
    key: 'qcAccuracyPercentage',
    direction: 'ASC',
  })

  useEffect(() => {
    setChapters(record?.chapters || [])
    if (record.chapters && record.chapters.length > 0) {
      setFilters({
        ...filters,
        chapterId: record.chapters[0].id,
      })
    }

    // Set initial voice selection when voices are available
    if (availableVoices.length > 0 && !selectedVoice) {
      setSelectedVoice(availableVoices[0])
    }
  }, [record?.chapters, availableVoices, selectedVoice])

  // Fetch sentences with server-side filtering (pagination is now client-side)
  const fetchSentences = async (currentFilters = filters) => {
    if (!record?.id) return

    try {
      setLoading(true)

      // Build filter object for API (only chapterId - accuracy filtering and pagination are client-side)
      const apiFilters = { bookId: record.id }

      if (currentFilters.chapterId) {
        apiFilters.chapterId = currentFilters.chapterId
      }

      const response = await dataProvider.getList('ai-book-sentences', {
        filter: apiFilters,
        pagination: { page: 1, perPage: 10000 }, // Fetch all records for client-side pagination
      })

      setSentences(response.data || [])
      // Total count is now the length of all fetched data for client-side pagination
    } catch (error) {
      console.error('Error fetching sentences:', error)
      notify('Error loading sentences', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  // Load sentences when component mounts or record changes
  useEffect(() => {
    fetchSentences()
  }, [record?.id])

  // Fetch sentences when server-side filters change (accuracy filters and pagination are client-side)
  useEffect(() => {
    if (record?.id) {
      fetchSentences(filters)
    }
  }, [filters.chapterId, record?.id])

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }))
    setPage(1) // Reset to first page when filters change
  }

  const handleSort = key => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'ASC' ? 'DESC' : 'ASC',
    }))
  }

  const handlePageChange = (event, newPage) => {
    setPage(newPage)
  }

  // Handle row click to show aside panel
  const handleRowClick = sentence => {
    setSelectedSentence(sentence)
    setShowAsidePanel(true)
    // Initialize form data with selected sentence values
    setSentenceFormData({
      speakRate: sentence.speakRate || '',
      contentWithBreaks: sentence.contentWithBreaks || sentence.content || '',
    })
  }

  // Handle closing aside panel
  const handleCloseAsidePanel = () => {
    setShowAsidePanel(false)
    setSelectedSentence(null)
    setSentenceFormData({
      speakRate: '',
      contentWithBreaks: '',
    })
  }

  // Handle reload sentence data
  const handleReloadSentence = async () => {
    try {
      if (!selectedSentence) {
        notify('No sentence selected', { type: 'error' })
        return
      }

      setLoading(true)

      // Fetch single sentence data using getOne with required parameters
      const response = await dataProvider.getOne('ai-book-sentences', {
        id: selectedSentence.id,
        bookId: record?.id,
        chapterId: selectedSentence.chapterId,
        paragraphId: selectedSentence.paragraphId,
      })

      if (response.data) {
        // Update the selected sentence with fresh data
        setSelectedSentence(response.data)

        // Update the form data with fresh data
        setSentenceFormData({
          speakRate: response.data.speakRate || '',
          contentWithBreaks:
            response.data.contentWithBreaks || response.data.content || '',
        })

        notify('Sentence data reloaded successfully', { type: 'success' })
      }
    } catch (error) {
      console.error('Error reloading sentence:', error)
      notify('Error reloading sentence data', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  // Handle sentence form field changes
  const handleSentenceChange = (field, value) => {
    setSentenceFormData(prev => ({ ...prev, [field]: value }))
  }

  // Handle Update Sentence And Trigger TTS
  const handleUpdateSentenceAndTriggerTTS = async () => {
    try {
      if (!selectedSentence) {
        notify('No sentence selected', { type: 'error' })
        return
      }

      setLoading(true)

      const payload = {
        contentWithBreaks:
          sentenceFormData.contentWithBreaks || selectedSentence.content,
        manualAudioPath: null,
        bookId: record?.id,
        chapterId: selectedSentence.chapterId,
        paragraphId: selectedSentence.paragraphId,
        speakRate: sentenceFormData.speakRate || 0,
        id: selectedSentence.id,
      }

      await dataProvider.update('ai-book-sentences', {
        id: selectedSentence.id,
        data: payload,
      })

      notify('Sentence updated and TTS triggered successfully', {
        type: 'success',
      })

      // Refresh the sentences list to get updated data
      await fetchSentences(filters, sortConfig)

      // Update the selected sentence with new data
      setSelectedSentence(prev => ({
        ...prev,
        ...payload,
        status: 'processing',
      }))

      refresh()
    } catch (error) {
      console.error('Error updating sentence and triggering TTS:', error)
      notify('Error updating sentence and triggering TTS', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  // Client-side filtering logic for accuracy
  const clientFilteredSentences = React.useMemo(() => {
    if (!sentences.length) return []

    return sentences.filter(sentence => {
      // Apply Min Accuracy filter
      if (filters.minAccuracy) {
        const minAccuracy = parseFloat(filters.minAccuracy)
        const sentenceAccuracy = parseFloat(sentence.qcAccuracyPercentage) || 0
        if (sentenceAccuracy < minAccuracy) {
          return false
        }
      }

      // Apply Max Accuracy filter
      if (filters.maxAccuracy) {
        const maxAccuracy = parseFloat(filters.maxAccuracy)
        const sentenceAccuracy = parseFloat(sentence.qcAccuracyPercentage) || 0
        if (sentenceAccuracy > maxAccuracy) {
          return false
        }
      }

      return true
    })
  }, [sentences, filters.minAccuracy, filters.maxAccuracy])

  // Client-side sorting logic
  const sortedSentences = React.useMemo(() => {
    if (!clientFilteredSentences.length) return []

    const sorted = [...clientFilteredSentences].sort((a, b) => {
      const { key, direction } = sortConfig
      let aValue = a[key]
      let bValue = b[key]

      // Handle different data types
      if (key === 'qcAccuracyPercentage') {
        aValue = parseFloat(aValue) || 0
        bValue = parseFloat(bValue) || 0
      } else if (
        key === 'orderIndex' ||
        key === 'chapterId' ||
        key === 'paragraphId'
      ) {
        aValue = parseInt(aValue) || 0
        bValue = parseInt(bValue) || 0
      } else {
        // String comparison for other fields
        aValue = String(aValue || '').toLowerCase()
        bValue = String(bValue || '').toLowerCase()
      }

      if (aValue < bValue) {
        return direction === 'ASC' ? -1 : 1
      }
      if (aValue > bValue) {
        return direction === 'ASC' ? 1 : -1
      }
      return 0
    })

    return sorted
  }, [clientFilteredSentences, sortConfig])

  // Client-side pagination - slice the sorted data based on current page
  const paginatedSentences = React.useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage
    const endIndex = startIndex + rowsPerPage
    return sortedSentences.slice(startIndex, endIndex)
  }, [sortedSentences, page, rowsPerPage])

  const totalPages = Math.ceil(sortedSentences.length / rowsPerPage)

  const getStatusColor = status => {
    switch (status) {
      case 'completed':
        return 'success'
      case 'error':
        return 'error'
      case 'processing':
        return 'warning'
      default:
        return 'default'
    }
  }

  const getAccuracyColor = percentage => {
    if (percentage >= 90) return '#4caf50' // green
    if (percentage >= 70) return '#ff9800' // orange
    return '#f44336' // red
  }

  if (!record?.id) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6">No book selected</Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 3 }}>
            All Sentences ({sortedSentences.length} of {sentences.length})
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {availableVoices.length > 0 && (
              <Grid item xs={12} md={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Voice</InputLabel>
                  <Select
                    value={selectedVoice}
                    label="Voice"
                    onChange={e => setSelectedVoice(e.target.value)}>
                    {availableVoices.map(voice => (
                      <MenuItem key={voice} value={voice}>
                        {voice}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} md={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Chapter ID</InputLabel>
                <Select
                  value={filters.chapterId}
                  label="Chapter ID"
                  onChange={e =>
                    handleFilterChange('chapterId', e.target.value)
                  }>
                  <MenuItem value="">All Chapters</MenuItem>
                  {chapters.map(chapter => (
                    <MenuItem key={chapter.id} value={chapter.id}>
                      Chapter {chapter.orderIndex}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label="Min Accuracy %"
                variant="outlined"
                size="small"
                type="number"
                fullWidth
                value={filters.minAccuracy}
                onChange={e =>
                  handleFilterChange('minAccuracy', e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label="Max Accuracy %"
                variant="outlined"
                size="small"
                type="number"
                fullWidth
                value={filters.maxAccuracy}
                onChange={e =>
                  handleFilterChange('maxAccuracy', e.target.value)
                }
              />
            </Grid>
          </Grid>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={sortConfig.key === 'orderIndex'}
                          direction={
                            sortConfig.key === 'orderIndex'
                              ? sortConfig.direction.toLowerCase()
                              : 'asc'
                          }
                          onClick={() => handleSort('orderIndex')}>
                          Order
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Chapter</TableCell>
                      <TableCell>Paragraph</TableCell>
                      <TableCell>Content</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortConfig.key === 'qcAccuracyPercentage'}
                          direction={
                            sortConfig.key === 'qcAccuracyPercentage'
                              ? sortConfig.direction.toLowerCase()
                              : 'asc'
                          }
                          onClick={() => handleSort('qcAccuracyPercentage')}>
                          QC Accuracy %
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedSentences.map(sentence => {
                      const voiceData = getVoiceSentenceData(
                        sentence,
                        selectedVoice,
                      )
                      return (
                        <TableRow
                          key={`${sentence.chapterId}-${sentence.paragraphId}-${sentence.orderIndex}`}
                          hover
                          onClick={() => handleRowClick(sentence)}
                          sx={{ cursor: 'pointer' }}>
                          <TableCell>{sentence.orderIndex}</TableCell>
                          <TableCell>{sentence.chapterId}</TableCell>
                          <TableCell>{sentence.paragraphId}</TableCell>
                          <TableCell sx={{ maxWidth: 300 }}>
                            <Typography variant="body2" noWrap>
                              {sentence.content}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={voiceData.status || 'N/A'}
                              color={getStatusColor(voiceData.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                color: getAccuracyColor(
                                  voiceData.qcAccuracyPercentage || 0,
                                ),
                                fontWeight: 'bold',
                              }}>
                              {voiceData.qcAccuracyPercentage != null
                                ? `${voiceData.qcAccuracyPercentage}%`
                                : 'N/A'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Aside Right Panel */}
      <Drawer
        anchor="right"
        open={showAsidePanel}
        onClose={handleCloseAsidePanel}
        sx={{
          '& .MuiDrawer-paper': {
            width: 500,
            padding: 2,
          },
        }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}>
          <Typography variant="h6">Sentence Details</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              onClick={handleReloadSentence}
              title="Reload sentence data">
              <RefreshIcon />
            </IconButton>
            <IconButton onClick={handleCloseAsidePanel}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {selectedSentence ? (
          <Box sx={{ height: '100%', overflow: 'auto' }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Chapter ID"
                  variant="outlined"
                  fullWidth
                  value={selectedSentence.chapterId || ''}
                  disabled
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  label="Order Index"
                  variant="outlined"
                  fullWidth
                  value={selectedSentence.orderIndex || ''}
                  disabled
                />
              </Grid>

              <Grid item xs={3}>
                <TextField
                  label="Paragraph ID"
                  variant="outlined"
                  fullWidth
                  value={selectedSentence.paragraphId || ''}
                  disabled
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Sentence Text"
                  multiline
                  rows={4}
                  variant="outlined"
                  fullWidth
                  value={selectedSentence.content || ''}
                  disabled
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Content with Breaks
                </Typography>
                <TextField
                  multiline
                  rows={4}
                  variant="outlined"
                  fullWidth
                  value={sentenceFormData.contentWithBreaks}
                  onChange={e =>
                    handleSentenceChange('contentWithBreaks', e.target.value)
                  }
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#f9f9f9',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Diff with STT Content
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    p: 1,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    backgroundColor: '#f9f9f9',
                    minHeight: '60px',
                  }}>
                  {getVoiceSentenceData(
                    selectedSentence,
                    selectedVoice,
                  )?.qcDiffResult?.map((item, index) => (
                    <span
                      key={index}
                      style={{
                        color:
                          item.type === 'removed'
                            ? 'red'
                            : item.type === 'added'
                              ? 'green'
                              : 'black',
                      }}>
                      {item.value + ' '}
                    </span>
                  )) || 'No diff data available'}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Speak Rate"
                  variant="outlined"
                  fullWidth
                  value={
                    getVoiceSentenceData(selectedSentence, selectedVoice)
                      ?.speakRate || ''
                  }
                  onChange={e =>
                    handleSentenceChange('speakRate', e.target.value)
                  }
                  type="number"
                  inputProps={{ step: 0.1, min: 0.1, max: 4.0 }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="QC Accuracy Percentage"
                  variant="outlined"
                  fullWidth
                  value={
                    getVoiceSentenceData(selectedSentence, selectedVoice)
                      ?.qcAccuracyPercentage || ''
                  }
                  disabled
                />
              </Grid>
              <Grid item xs={4}>
                <Typography variant="subtitle2" gutterBottom>
                  Status
                </Typography>
                <Chip
                  label={
                    getVoiceSentenceData(selectedSentence, selectedVoice)
                      ?.status || 'N/A'
                  }
                  color={getStatusColor(
                    getVoiceSentenceData(selectedSentence, selectedVoice)
                      ?.status,
                  )}
                  variant="outlined"
                />
              </Grid>
              {getVoiceAudioPath(selectedSentence, selectedVoice) && (
                <Grid item xs={12}>
                  <CustomAudioField
                    label="Sentence Audio"
                    audioPath={getVoiceAudioPath(
                      selectedSentence,
                      selectedVoice,
                    )}
                    inline={true}
                  />
                </Grid>
              )}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleUpdateSentenceAndTriggerTTS}
                  disabled={loading}
                  fullWidth
                  sx={{ py: 1.5 }}>
                  {loading
                    ? 'Processing...'
                    : 'Update Sentence And Trigger TTS'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No sentence selected
          </Typography>
        )}
      </Drawer>
    </Box>
  )
}

export default SentencesTab
